import { ContextRegistry } from '../../../core/src/services/ContextRegistry';
import { AIAction, AIModule } from '../types/module-registry';
import { resolveContextValue } from './ContextValueResolver';
import { ModuleRegistry } from './ModuleRegistry';

export interface AIWorkflowStep {
	actionName: string;
	params: Record<string, any>;
	dependsOn?: string[]; // Previous step names that must complete first
	conditional?: {
		condition: string; // JS expression to evaluate
		skipIf?: boolean; // Skip this step if condition is true
	};
}

export interface AIWorkflow {
	name: string;
	description: string;
	steps: AIWorkflowStep[];
	context?: Record<string, any>;
}

export interface AIWorkflowResult {
	success: boolean;
	steps: Array<{
		stepName: string;
		actionName: string;
		success: boolean;
		result?: any;
		error?: string;
		skipped?: boolean;
	}>;
	finalResult?: any;
	error?: string;
}

class AIRegistryClass {
	private modules = new Map<string, AIModule>();
	private workflows = new Map<string, AIWorkflow>();
	private listeners = new Set<() => void>();

	/**
	 * Register an AI module with its actions
	 */
	registerModule(aiModule: AIModule): void {
		this.modules.set(aiModule.name, aiModule);
		this.notifyListeners();
	}

	/**
	 * Get a specific AI module
	 */
	getModule(name: string): AIModule | undefined {
		return this.modules.get(name);
	}

	/**
	 * Get all registered AI modules
	 */
	getAllModules(): AIModule[] {
		return Array.from(this.modules.values());
	}

	/**
	 * Get all actions, optionally filtered by category
	 */
	getActions(category?: string): AIAction[] {
		const allActions: AIAction[] = [];
		for (const module of this.modules.values()) {
			const filteredActions = category
				? module.actions.filter((action) => action.category === category)
				: module.actions;
			allActions.push(...filteredActions);
		}
		return allActions;
	}

	/**
	 * Get a specific action by name
	 */
	getAction(name: string): { action: AIAction; module: AIModule } | undefined {
		for (const module of this.modules.values()) {
			const action = module.actions.find((a) => a.name === name);
			if (action) {
				return { action, module };
			}
		}
		return undefined;
	}

	/**
	 * Execute a single AI action
	 */
	async executeAction(
		actionName: string,
		params: Record<string, any>,
		context?: Record<string, any>,
	): Promise<any> {
		try {
			// Find the action
			const result = this.getAction(actionName);
			if (!result) {
				return {
					success: false,
					error: {
						type: 'ACTION_NOT_FOUND',
						message: `AI action '${actionName}' not found`,
						details: {
							availableActions: this.getActions().map((a) => a.name),
							requestedAction: actionName,
						},
					},
				};
			}

			const { action, module } = result;

			// Get current context from module if needed
			let fullContext = context || {};
			if (module.contextProvider) {
				try {
					const moduleContext = module.contextProvider();
					fullContext = { ...fullContext, ...moduleContext };
				} catch (error) {
					// Warning: Failed to get context from module
					// Continue without module context
				}
			}

			// Add global context from ContextRegistry
			fullContext = {
				...fullContext,
				...ContextRegistry.getCurrentContext(),
			};

			// Resolve context values in parameters
			let finalParams = { ...params };
			const unresolvedContextValues: string[] = [];

			for (const [paramName, paramValue] of Object.entries(finalParams)) {
				const resolved = resolveContextValue(
					paramValue,
					paramName,
					action,
					fullContext,
				);

				if (resolved !== undefined) {
					finalParams[paramName] = resolved;
				} else if (
					typeof paramValue === 'string' &&
					paramValue.startsWith('@')
				) {
					// Warning: Could not resolve context value
					unresolvedContextValues.push(`${paramName}: ${paramValue}`);
					// Keep the original value - let validation handle it
				}
			}

			// Validate required parameters after context resolution
			const missingParams: string[] = [];
			const invalidParams: Record<string, string> = {};

			for (const param of action.parameters) {
				const value = finalParams[param.name];

				// Check if required parameter is missing
				if (
					param.required &&
					(value === undefined || value === null || value === '')
				) {
					missingParams.push(param.name);
				}

				// Type validation for provided values
				if (value !== undefined && value !== null) {
					// Special case: numbers can be converted to strings
					if (param.type === 'string' && typeof value === 'number') {
						// Convert number to string
						finalParams[param.name] = String(value);
					} else {
						const isValid = this.validateParameterType(value, param.type);
						if (!isValid) {
							invalidParams[param.name] =
								`Expected ${param.type}, got ${typeof value}`;
						}
					}
				}
			}

			// Check for unresolved context values in required parameters
			for (const param of action.parameters) {
				if (
					param.required &&
					typeof finalParams[param.name] === 'string' &&
					finalParams[param.name].startsWith('@')
				) {
					const contextKey = finalParams[param.name].substring(1);
					missingParams.push(param.name);
					unresolvedContextValues.push(
						`${param.name} requires context value '${contextKey}'`,
					);
				}
			}

			// Return structured error if validation fails
			if (
				missingParams.length > 0 ||
				Object.keys(invalidParams).length > 0 ||
				unresolvedContextValues.length > 0
			) {
				return {
					success: false,
					error: {
						type: 'VALIDATION_ERROR',
						message: `Validation failed for action '${actionName}'`,
						details: {
							missingParameters: missingParams,
							invalidParameters: invalidParams,
							unresolvedContextValues: unresolvedContextValues,
							requiredParameters: action.parameters
								.filter((p) => p.required)
								.map((p) => ({
									name: p.name,
									type: p.type,
									description: p.description,
								})),
							availableContext: Object.keys(fullContext),
						},
					},
				};
			}

			// Validate required context
			if (action.contextRequired) {
				const missingContext: string[] = [];
				for (const reqContext of action.contextRequired) {
					if (!fullContext[reqContext]) {
						missingContext.push(reqContext);
					}
				}

				if (missingContext.length > 0) {
					return {
						success: false,
						error: {
							type: 'MISSING_CONTEXT',
							message: `Required context missing for action '${actionName}'`,
							details: {
								missingContext: missingContext,
								availableContext: Object.keys(fullContext),
								hint: 'These context values must be set before executing this action',
							},
						},
					};
				}
			}

			// Execute the action
			try {
				const actionResult = await action.execute(finalParams, fullContext);

				// Store result values in context if mapping is defined
				if (action.resultContextMapping && actionResult) {
					const contextUpdates: Record<string, any> = {};

					for (const [contextKey, resultPath] of Object.entries(
						action.resultContextMapping,
					)) {
						const value = this.getValueByPath(actionResult, resultPath);
						if (value !== undefined) {
							contextUpdates[contextKey] = value;
						}
					}

					if (Object.keys(contextUpdates).length > 0) {
						ContextRegistry.setMultiple(contextUpdates, actionName);
					}
				}

				// Return success with result
				return {
					success: true,
					result: actionResult,
					action: actionName,
					contextUpdated: action.resultContextMapping
						? Object.keys(action.resultContextMapping)
						: [],
				};
			} catch (executionError) {
				// Handle execution errors
				// // Error: Action execution failed

				return {
					success: false,
					error: {
						type: 'EXECUTION_ERROR',
						message: `Action '${actionName}' failed during execution`,
						details: {
							error:
								executionError instanceof Error
									? executionError.message
									: String(executionError),
							action: actionName,
							parameters: finalParams,
							hint: 'Check the action implementation and parameters',
						},
					},
				};
			}
		} catch (unexpectedError) {
			// Handle any unexpected errors
			// // Error: Unexpected error in executeAction

			return {
				success: false,
				error: {
					type: 'UNEXPECTED_ERROR',
					message: 'An unexpected error occurred',
					details: {
						error:
							unexpectedError instanceof Error
								? unexpectedError.message
								: String(unexpectedError),
						action: actionName,
					},
				},
			};
		}
	}

	/**
	 * Validate parameter type
	 */
	private validateParameterType(value: any, expectedType: string): boolean {
		switch (expectedType) {
			case 'string':
				return typeof value === 'string';
			case 'number':
				return typeof value === 'number' && !isNaN(value);
			case 'boolean':
				return typeof value === 'boolean';
			case 'array':
				return Array.isArray(value);
			case 'object':
				return (
					typeof value === 'object' && value !== null && !Array.isArray(value)
				);
			case 'any':
				return true;
			default:
				// For custom types, just check if value exists
				return value !== undefined && value !== null;
		}
	}

	/**
	 * Get value from object by dot-notation path (supports array notation)
	 */
	private getValueByPath(obj: any, path: string): any {
		const keys = path.split(/[.\[\]]/).filter((k) => k !== '');
		let result = obj;

		for (const key of keys) {
			if (result === null || result === undefined) {
				return undefined;
			}
			result = result[key];
		}

		return result;
	}

	/**
	 * Get current context from all modules
	 */
	getCurrentContext(): Record<string, any> {
		const context: Record<string, any> = {};

		// Always include currentPath
		try {
			// Get current path from browser location or ModuleRegistry
			context['currentPath'] = window.location.pathname || '/';
		} catch (e) {
			// Fallback if window is not available
			context['currentPath'] = ModuleRegistry.getCurrentPath() || '/';
		}

		// Get context from all modules
		for (const module of this.modules.values()) {
			if (module.contextProvider) {
				try {
					const moduleContext = module.contextProvider();
					Object.assign(context, moduleContext);
				} catch (error) {
					// Warning: Failed to get context from module
				}
			}
		}

		// Merge with ContextRegistry values
		const registryContext = ContextRegistry.getCurrentContext();
		Object.assign(context, registryContext);

		return context;
	}

	/**
	 * Register a workflow
	 */
	registerWorkflow(workflow: AIWorkflow): void {
		this.workflows.set(workflow.name, workflow);
	}

	/**
	 * Execute a multi-step workflow
	 */
	async executeWorkflow(
		workflowName: string,
		initialContext?: Record<string, any>,
	): Promise<AIWorkflowResult> {
		try {
			const workflow = this.workflows.get(workflowName);
			if (!workflow) {
				return {
					success: false,
					steps: [],
					error: `Workflow '${workflowName}' not found. Available workflows: ${Array.from(this.workflows.keys()).join(', ')}`,
				};
			}

			const result: AIWorkflowResult = {
				success: true,
				steps: [],
			};

			let workflowContext = {
				...this.getCurrentContext(),
				...workflow.context,
				...initialContext,
			};
			const stepResults = new Map<string, any>();

			for (let i = 0; i < workflow.steps.length; i++) {
				const step = workflow.steps[i];
				if (step == null) {
					// Unreachable: i is bounded by steps.length
					continue;
				}
				const stepName = `step_${i}`;
				const stepResult: any = {
					stepName: stepName,
					actionName: step.actionName,
					success: false,
				};

				try {
					// Check dependencies
					if (step.dependsOn) {
						for (const dependency of step.dependsOn) {
							if (!stepResults.has(dependency)) {
								stepResult.error = `Step depends on '${dependency}' which hasn't completed`;
								stepResult.success = false;
								result.steps.push(stepResult);
								result.success = false;
								result.error = `Workflow step ${i} failed: ${stepResult.error}`;
								return result;
							}
						}
					}

					// Check conditional
					if (step.conditional) {
						const shouldSkip = this.evaluateCondition(
							step.conditional.condition,
							workflowContext,
						);
						if (
							(step.conditional.skipIf && shouldSkip) ||
							(!step.conditional.skipIf && !shouldSkip)
						) {
							stepResult.skipped = true;
							stepResult.success = true;
							result.steps.push(stepResult);
							continue;
						}
					}

					// Execute the action - now returns structured result
					const actionResult = await this.executeAction(
						step.actionName,
						step.params,
						workflowContext,
					);

					if (actionResult.success) {
						stepResult.success = true;
						stepResult.result = actionResult.result;
						stepResults.set(stepName, actionResult.result);

						// Update context with step result
						workflowContext['lastStepResult'] = actionResult.result;
						workflowContext[`step_${i}_result`] = actionResult.result;
					} else {
						// Handle structured error from executeAction
						stepResult.success = false;
						stepResult.error = actionResult.error.message;
						stepResult.errorDetails = actionResult.error;
						result.success = false;
						result.error = `Workflow step ${i} (${step.actionName}) failed: ${actionResult.error.message}`;

						// Include the full error details for AI understanding
						if (!result.steps) result.steps = [];
						result.steps.push(stepResult);

						// Stop on failure
						break;
					}
				} catch (error) {
					stepResult.success = false;
					stepResult.error =
						error instanceof Error ? error.message : String(error);
					stepResult.errorDetails = {
						type: 'WORKFLOW_STEP_ERROR',
						message: `Unexpected error in workflow step ${i}`,
						details: {
							step: i,
							action: step.actionName,
							error: error instanceof Error ? error.message : String(error),
						},
					};
					result.success = false;
					result.error = `Workflow step ${i} failed: ${stepResult.error}`;
				}

				result.steps.push(stepResult);

				// Stop on failure unless workflow allows continuation
				if (!stepResult.success && !stepResult.skipped) {
					break;
				}
			}

			// Set final result to last successful step result
			const lastSuccessfulStep = result.steps
				.slice()
				.reverse()
				.find((s) => s.success && !s.skipped);
			if (lastSuccessfulStep) {
				result.finalResult = lastSuccessfulStep.result;
			}

			return result;
		} catch (unexpectedError) {
			// // Error: Unexpected error in executeWorkflow

			return {
				success: false,
				steps: [],
				error: `Unexpected error executing workflow '${workflowName}': ${unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError)}`,
			};
		}
	}

	/**
	 * Simple condition evaluator (basic implementation)
	 */
	private evaluateCondition(
		condition: string,
		context: Record<string, any>,
	): boolean {
		try {
			// Simple evaluation - in production, use a proper expression evaluator
			const func = new Function(
				'context',
				`with(context) { return ${condition}; }`,
			);
			return !!func(context);
		} catch (error) {
			// Warning: Failed to evaluate condition
			return false;
		}
	}

	/**
	 * Subscribe to registry changes
	 */
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach((listener) => listener());
	}

	/**
	 * Get debug info about registered modules and actions
	 */
	getDebugInfo(): any {
		return {
			modules: Array.from(this.modules.keys()),
			actions: this.getActions().map((a) => ({
				name: a.name,
				category: a.category,
				description: a.description,
				parameters: a.parameters.length,
				contextRequired: a.contextRequired?.length || 0,
			})),
			workflows: Array.from(this.workflows.keys()),
		};
	}
}

export const AIRegistry = new AIRegistryClass();
